import { createFileRoute } from "../lib/router-compat";
import { PageShell, SoundCloudEmbed } from "../components/PageShell";

export const Route = createFileRoute("/recordings")({
  head: () => ({
    meta: [
      { title: "Recordings — The Pan Institute" },
      { name: "description", content: "Recordings from St. Mary's Heavenly Fire, Planet Pan, Atlantic Clarion, and more." },
    ],
  }),
  component: RecordingsPage,
});

const USER = "https://soundcloud.com/nigel-chase-515097568";
const SECTIONS: { era: string; years: string; band: string; albums: { title: string; year?: string; url: string }[] }[] = [
  {
    era: "St. Mary's Heavenly Fire Steelband",
    years: "1995 – 2001",
    band: "Liner notes by Father Ed Waldron, founder of St. Mary's Heavenly Fire Steelband.",
    albums: [
      { title: "St. Mary's Heavenly Fire Steel Band", year: "1997", url: `${USER}/sets/st-marys-heavenly-fire-steel-band` },
      { title: "Playing with Fire", year: "1999", url: `${USER}/sets/playing-with-fire-4` },
      { title: "Heavenly Fire & The Fine Arts Chorale", url: `${USER}/sets/st-marys-heanvenly-fire-steel-band-with-the-fine-arts-chorale` },
      { title: "Spontaneous Combustion", year: "2001", url: `${USER}/sets/spontaneous-combustion` },
      { title: "Holy Smoke", year: "2001", url: `${USER}/sets/holy-smoke` },
    ],
  },
  {
    era: "Planet Pan Steel Band - Rhythm Rockets",
    years: "2004 – 2017",
    band: "Recordings from the youth ensembles directed by Nigel Chase.",
    albums: [
      { title: "Planet Pan / Rhythm Rockets", year: "2006 – 2007", url: `${USER}/sets/planet-pan-rhythm-rockets-2006-2007` },
    ],
  },
  {
    era: "Atlantic Clarion Steel Band",
    years: "1988 – 2024",
    band: "Three decades of recordings from Maine's premier steel band.",
    albums: [],
  },
];

const totalAlbums = SECTIONS.reduce((n, s) => n + s.albums.length, 0);

function RecordingsPage() {
  return (
    <PageShell
      tag=""
      title={<>The <span className="text-primary">recordings</span>.</>}
      intro="Three decades of steel band music — Heavenly Fire, Planet Pan, Rhythm Rockets, and Atlantic Clarion."
    >
      {/* Intro card */}
      <div className="rounded-3xl border border-border/40 bg-gradient-to-br from-primary/10 via-card/40 to-transparent p-8 md:p-12">
        <div className="text-[10px] uppercase tracking-[0.5em] text-primary">Streaming on SoundCloud</div>
        <p className="mt-3 font-display italic text-2xl md:text-4xl leading-[1.1] max-w-3xl">
          Three decades of steel band music — pressed, archived, and ready to play.
        </p>
        <a
          href={USER}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center justify-center px-6 py-2.5 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20"
        >
          Follow on SoundCloud
        </a>
      </div>

      {/* Stats strip */}
      <dl className="mt-10 grid grid-cols-3 divide-x divide-border/40 rounded-2xl border border-border/40 bg-card/30">
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-3xl md:text-5xl text-primary leading-none">{SECTIONS.length}</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">Eras</dd>
        </div>
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-3xl md:text-5xl text-primary leading-none">{totalAlbums}</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">Albums</dd>
        </div>
        <div className="px-4 py-6 text-center">
          <dt className="font-display italic text-3xl md:text-5xl text-primary leading-none">36</dt>
          <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">Years on tape</dd>
        </div>
      </dl>

      {/* Catalog */}
      <div className="mt-20 space-y-24">
        {SECTIONS.map((s, idx) => (
          <section key={s.era}>
            <div className="flex items-end justify-between gap-6 mb-8 border-b border-border/40 pb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
                  {String(idx + 1).padStart(2, "0")} · {s.years}
                </div>
                <h2 className="mt-3 font-display italic text-3xl md:text-4xl">{s.era}</h2>
                <p className="mt-2 text-sm text-foreground/60 max-w-2xl">{s.band}</p>
              </div>
              <div className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-foreground/40 whitespace-nowrap">
                {String(s.albums.length).padStart(2, "0")} {s.albums.length === 1 ? "album" : "albums"}
              </div>
            </div>

            {s.albums.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border/50 bg-card/20 p-10 text-center">
                <div className="text-[10px] uppercase tracking-[0.4em] text-primary/70">Coming soon</div>
                <p className="mt-3 font-display italic text-2xl">Archive in progress</p>
                <p className="mt-2 text-sm text-foreground/60">Tapes being digitized — new releases on the way.</p>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {s.albums.map((a, i) => (
                  <article key={a.url} className="group">
                    <div className="flex items-baseline gap-4 mb-3">
                      <span className="font-display italic text-2xl text-primary/40 leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display italic text-xl truncate">{a.title}</h3>
                        {a.year && (
                          <div className="text-[10px] uppercase tracking-[0.35em] text-foreground/50 mt-1">{a.year}</div>
                        )}
                      </div>
                    </div>
                    <SoundCloudEmbed url={a.url} title={a.title} />
                  </article>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      <p className="mt-16 text-xs text-foreground/50 italic text-center">
        Liner notes by Father Ed Waldron, founder of St. Mary's Heavenly Fire Steelband.
      </p>
    </PageShell>
  );
}